/**
 * Moon Phase Components
 *
 * Display moon phase indicators for calendar views
 */

import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/components/ui/Tooltip';
import { getMoonPhase } from '@/utils/moonPhase';

interface MoonPhaseProps {
  date: Date;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

const SIZE_CLASSES = {
  sm: 'text-xs',
  md: 'text-base',
  lg: 'text-2xl',
};

/**
 * Simple moon phase indicator (just the icon)
 */
export const MoonPhaseIndicator = ({
  date,
  size = 'md',
  showLabel = false,
  className = '',
}: MoonPhaseProps) => {
  const { t } = useTranslation('common');
  const phase = getMoonPhase(date);

  if (showLabel) {
    return (
      <Tooltip content={t('moon_phase_illuminated', { label: phase.label, illumination: phase.illumination })}>
        <span
          className={`inline-flex items-center gap-1 ${className}`}
          aria-label={t('moon_phase_label', { label: phase.label })}
        >
          <span className={SIZE_CLASSES[size]}>{phase.icon}</span>
          <span className="text-xs text-gray-500">{phase.label}</span>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={t('moon_phase_illuminated', { label: phase.label, illumination: phase.illumination })}>
      <span
        className={`${SIZE_CLASSES[size]} ${className}`}
        aria-label={t('moon_phase_label', { label: phase.label })}
      >
        {phase.icon}
      </span>
    </Tooltip>
  );
};

/**
 * Detailed moon phase display with label and illumination
 */
export const MoonPhaseDisplay = ({
  date,
  size = 'lg',
  className = '',
}: Omit<MoonPhaseProps, 'showLabel'>) => {
  const { t } = useTranslation('common');
  const phase = getMoonPhase(date);

  return (
    <div
      className={`flex flex-col items-center gap-1 ${className}`}
      aria-label={t('moon_phase_illuminated', { label: phase.label, illumination: phase.illumination })}
    >
      <span className={SIZE_CLASSES[size]}>{phase.icon}</span>
      <div className="text-center">
        <div className="text-sm font-medium text-gray-700">{phase.label}</div>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <div className="text-xs text-gray-500">{phase.illumination}% illuminated</div>
      </div>
    </div>
  );
};
